import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import SEO from '../components/SEO';
import { Clock, Tag, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog`);
        if (!cancelled && data?.success) setPosts(data.posts || []);
      } catch (e) {
        console.error('Failed to load blog posts', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const blogListLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Connectify Travel Blog',
    itemListElement: posts.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://connectify-esims.com/blog/${p.slug}`,
      name: p.title,
    })),
  };

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950">
      <SEO
        title="Travel & eSIM Blog — Guides, Tips & News"
        description="In-depth eSIM travel guides, country-by-country data plan comparisons, and traveller tips from the Connectify team. Everything you need to stay connected abroad."
        keywords="eSIM blog, travel data guide, eSIM tips, prepaid eSIM guide, international roaming blog, travel connectivity"
        canonical="/blog"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' },
        ]}
        structuredData={posts.length > 0 ? [blogListLd] : []}
      />
      <Navbar />

      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-12 text-center" data-testid="blog-hero">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 dark:text-neutral-100 mb-4">
              Travel & eSIM Blog
            </h1>
            <p className="text-base sm:text-lg text-gray-600 dark:text-neutral-400 max-w-2xl mx-auto">
              Guides, comparisons, and tips to help you stay connected wherever you go.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-neutral-500" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-empty">
              <p className="text-gray-600 dark:text-neutral-400">No posts yet — check back soon.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:border-black dark:hover:border-white transition-colors"
                >
                  {p.header_image ? (
                    <div className="aspect-[16/9] overflow-hidden bg-gray-100 dark:bg-neutral-800">
                      <img
                        src={p.header_image}
                        alt={p.header_image_alt || p.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-neutral-100 to-neutral-200 dark:from-neutral-800 dark:to-neutral-900" />
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-2 line-clamp-2 group-hover:underline">
                      {p.title}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-neutral-400 mb-4 line-clamp-3 flex-1">
                      {p.excerpt || p.meta_description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-neutral-500">
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {p.reading_time_minutes || 1} min read
                      </span>
                      {p.tags && p.tags[0] && (
                        <span className="flex items-center gap-1">
                          <Tag size={12} /> {p.tags[0]}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
